import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { HomeworkService } from '../services/homeworkService';
import { HomeworkQueryService, HomeworkFile } from '../services/homeworkQueryService';

export const useHomework = (sessionId: number | null) => {
  const { user } = useAuth();
  const [homeworkFiles, setHomeworkFiles] = useState<HomeworkFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load homework files when session changes
  useEffect(() => {
    console.log('useHomework useEffect triggered with sessionId:', sessionId);
    
    if (!sessionId) {
      console.log('No sessionId, clearing homework files');
      setHomeworkFiles([]);
      return;
    }

    const loadHomeworkFiles = async () => {
      console.log('Loading homework files for session:', sessionId);
      setIsLoading(true);
      setError(null);
      try {
        const files = await HomeworkQueryService.getHomeworkFiles(sessionId);
        console.log('Setting homework files for session', sessionId, ':', files.length, 'files');
        setHomeworkFiles(files);
      } catch (err) {
        setError('Failed to load homework files');
        console.error('Error loading homework files:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadHomeworkFiles();
  }, [sessionId]);

  const uploadHomework = async (file: File) => {
    if (!sessionId || !user) return;

    setIsUploading(true);
    setError(null);

    try {
      const uploadedFile = await HomeworkService.uploadHomework(sessionId, file, user.id, user.email);
      setHomeworkFiles(prev => [uploadedFile, ...prev]);
      return true;
    } catch (err) {
      setError('Failed to upload homework file');
      console.error('Error uploading homework:', err);
      return false;
    } finally {
      setIsUploading(false);
    }
  };

  const deleteHomework = async (fileName: string) => {
    if (!sessionId || !user) return;

    try {
      await HomeworkService.deleteHomework(sessionId, fileName, user.id);
      setHomeworkFiles(prev => prev.filter(file => file.id !== fileName));
    } catch (err) {
      setError('Failed to delete homework file');
      console.error('Error deleting homework:', err);
    }
  };

  const clearError = () => {
    setError(null);
  };

  return {
    homeworkFiles,
    isUploading,
    isLoading,
    error,
    uploadHomework,
    deleteHomework,
    clearError,
  };
}; 